import Model from './model.js';
import config from 'config';
import moment from 'moment';
import axios from 'axios';
import Employees from 'newCollections/employees.js';
import Payslips from 'newCollections/payslips.js';
import Payrolls from 'newCollections/payrolls.js';
import PayrollDetails from 'newCollections/payroll-details.js';
import PayrollIntegrations from 'newCollections/payroll-integrations.js';
import Company from './company.js';

const FORMS_URL = config.apiUrl + `/GovernmentForms`;

export const payrollStatus = {
    INITIAL: 0, // step 1
    RAN: 1, // after done with run (beginning of step 2)
    CONFIRMED: 2, // after clicking "continue" at step 2
    SUBMITTED: 3,
    FINISHED: 9 // done
};

export default class Payroll extends Model {
    urlRoot() { return '/Payrolls'; }

    constraints() {
        return {
            companyId: {presence: true},
            status:  {
                presence: true,
                numericality: {
                    onlyInteger: true,
                    greaterThanOrEqualTo: 0,
                    lessOrEqualThan: 0
                }
            }
        };
    }

    defaults() {
        return {
            submittedDate:      null,
            chequePickupDate:   null,
            periodDate:         null,
            status:             0,
            lastEditedEmployee: null,
            employeeEpf:        0,
            employeeSocso:      0,
            employerEpf:        0,
            employerSocso:      0,
            mtd:                0,
            cp38:               0,
            zakat:              0,
            grossPay:           0,
            netPay:             0,
            base:               0,
            additions:          0,
            deductions:         0,
            payslips:           0
        };
    }

    constructor() {
        super(...arguments);
        this.softDelete = false;
    }

    initialize() {
        this.idAttribute = 'objectId';
        this.employees = new Employees(null, {
            company: app.company,
            pagination: false
        });
        this.payslips = new Payslips(null, {
            payroll: this,
            pagination: false
        });
        this.integrations = new PayrollIntegrations(null, {
            payroll: this,
            pagination: false
        });
        return super.initialize(...arguments);
    }

    fetchPayslips() {
        return this.payslips.fetch({filter: {limit: 9999}})
            .then(() => this.payslips);
    }

    fetchEmployees() {
        return new Payslips(null, {payroll: this, pagination: false})
            .fetch({filter: {limit: 9999}})
            .then(payslips => {
                const ids = payslips.map(p => p.get('employeeId'));
                return this.employees.fetch({filter: {
                    where: {companyId: this.get('companyId')},
                    limit: 9999
                }})
                    .then(employees => employees
                        .filter(emp => ids.includes(emp.get('objectId')))
                    );
            });
    }

    fetchCompany() {
        if (!this.get('companyId')) {
            return Promise.resolve(null);
        }

        this.company = new Company({objectId: this.get('companyId')});
        return this.company.fetch();
    }

    getCompany() {
        return this.company;
    }

    setCompany(company) {
        if (this.get('companyId') === company.id) {
            this.company = company;
        }
    }

    fetchIntegrations(filter = {}) {
        return this.integrations.fetch(filter).then(() => this.integrations);
    }

    getIntegrations() {
        return this.integrations;
    }

    samePeriodAs(payroll) {
        return this.getPeriod().isSame(payroll.getPeriod(), 'month');
    }

    isCurrentPeriod() {
        return (
            this.getPeriod().isSame(
                Payroll.getPeriodFromDate(Date.now()), 'month'
            )
        );
    }

    getStatusLabel() {
        if (this.get('status') < 3) {
            return 'Saved';
        } else if (this.get('status') === 9) {
            return 'Complete';
        } else {
            return 'Submitted';
        }
    }

    getPeriod() {
        if (this.get('periodDate')) {
            return moment(this.get('periodDate')).tz('Asia/Kuala_Lumpur');
        }

        return moment(Payroll.getPeriodFromDate(
            this.get('submittedDate') || Date.now()
        )).tz('Asia/Kuala_Lumpur');
    }

    getPeriodFormatted() {
        return this.getPeriod().format('MMMM YYYY');
    }

    isCreated() {
        return (this.get('status') || 0) === 0;
    }

    isComplete() {
        return (this.get('status') === 9);
    }

    hasPendingJobs() {
        return axios.get(
            `${this.finalUrl()}/hasPendingJobs`, {
                headers: {'Authorization': app.user.getAccessToken().id}
            }
        ).then(r => r.data)
            .catch(err => {
                throw err.response.data.error;
            });
    }

    generateForms() {
        return axios.post(
            `${FORMS_URL}/${this.id}/generatePayrollForms`,
            {},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this);
    }

    setStatus(status, chequePickupDate = null) {
        return axios.post(
            `${this.finalUrl()}/setStatus`,
            {status, chequePickupDate},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this.fetch())
            .catch(err => {
                if (err.response) {
                    throw err.response.data.error;
                }
                throw err;
            });
    }

    run(includeAllEmployees = true) {
        return axios.post(
            `${this.finalUrl()}/run`,
            {includeAllEmployees},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this.fetch())
            .then(() => {
                this.trigger('running', this);
                this.whenDone().then(() => {
                    this.trigger('run-complete', this);
                });
                return this;
            })
            .catch(err => {
                if (err.response) {
                    throw err.response.data.error;
                }
                throw err;
            });
    }

    whenDone() {
        let timeout;
        const resolver = Promise.defer();
        const pollStatus = () => {
            return axios.get(
                `${this.finalUrl()}/jobStatus`, {
                    headers: {'Authorization': app.user.getAccessToken().id}
                }
            ).then(res => {
                if (res.data.done === true) {
                    clearTimeout(timeout);
                    resolver.resolve(this);
                    return;
                }

                timeout = setTimeout(pollStatus, 2000);
            }).catch(err => {
                clearTimeout(timeout);
                if (err.response) {
                    resolver.reject(err.response.data.error);
                } else {
                    resolver.reject(err);
                }
            });
        };

        pollStatus();

        return resolver.promise;
    }

    revert(opts = {}) {
        return axios.post(
            `${this.finalUrl()}/revert`,
            opts,
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this.fetch());
    }

    resetUnpaidLeave(opts = {}) {
        return axios.post(
            `${this.finalUrl()}/resetUnpaidLeave`,
            opts,
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this.fetch());
    }

    resetEmployee({employee = null} = {}) {
        let employeeId = null;
        if (employee) {
            employeeId = employee.id;
        }
        return axios.post(
            `${this.finalUrl()}/resetEmployee`,
            {employeeId},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        )
            .then(() => {
                if (!employee) {
                    return null;
                }

                const overtimeItems = app.payrollItems
                    .filter(p => p.get('type') === 'overtime')
                    .map(p => p.id);

                const details = new PayrollDetails(null, {
                    payroll: this,
                    filter: {limit: 9999},
                    pagination: false
                });
                return details.fetch({filter: {where: {
                    employeeId: employeeId,
                    payrollItemId: {inq: overtimeItems}
                }}});
            })
            .then(details => this.fixOvertimeAfterReset(details, employee))
            .catch(err => {
                if (err.response) {
                    throw err.response.data.error;
                }
                throw err;
            });
    }

    reset() {
        return axios.post(
            `${this.finalUrl()}/reset`,
            {},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        )
            .then(() => {
                const overtimeItems = app.payrollItems
                    .filter(p => p.get('type') === 'overtime')
                    .map(p => p.id);

                const details = new PayrollDetails(null, {
                    payroll: this,
                    filter: {limit: 9999},
                    pagination: false
                });

                return details.fetch({filter: {where: {
                    payrollItemId: {inq: overtimeItems}
                }}});
            })
            .then(details => this.fixOvertimeAfterReset(details))
            .catch(err => {
                if (err.response) {
                    throw err.response.data.error;
                }
                throw err;
            });
    }

    fetchAllPayrollDetails() {
        return new PayrollDetails(null, {payroll:this, pagination:false})
            .fetch({filter: {limit: 9999}}).then(details => {
                return details;
            })
    }

    fixOvertimeAfterReset(details, employee = null) {
        return Promise.all(details.map(detail => {
            return detail.createOvertimeInfo(this, employee)
                .then(detail => detail.save());
        }));
    }

    submit(opts = {}) {
        if (opts.chequePickupDate === null) {
            delete opts.chequePickupDate;
        }
        return axios.post(
            `${this.finalUrl()}/submit`,
            opts,
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this.fetch())
            .catch(err => {
                if (err.response) {
                    throw err.response.data.error;
                }
                if (err.message === 'Network Error') {
                    return this.fetch();
                }
                throw err;
            });
    }

    sendPayslips(payslips) {
        return axios.post(
            `${this.finalUrl()}/sendPayslips`,
            {payslips},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this)
            .catch(err => {
                throw err.response.data.error;
            });
    }

    calculateBillableAmount() {
        return axios.get(
            `${this.finalUrl()}/billableAmount`,
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(r => r.data)
            .catch(err => {
                throw err.response.data.error;
            });
    }

    finish(opts = {}) {
        return axios.post(
            `${this.finalUrl()}/finish`,
            opts,
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this.fetch())
            .catch(err => {
                throw err.response.data.error;
            });
    }

    createArchive() {
        return axios.post(
            `${this.finalUrl()}/archive`,
            {},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this)
            .catch(err => {
                throw err.response.data.error;
            });
    }

    markAsManuallyInvoiced() {
        return axios.post(
            `${this.finalUrl()}/markAsManuallyInvoiced`,
            {},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this)
            .catch(err => {
                throw err.response.data.error;
            });
    }

    syncPayroll(integration = null) {
        if (integration === null) {
            throw new Error(`Can't integrate payroll to an unknown provider`);
        }

        const provider = integration.get('provider');
        return axios.post(
            `${this.finalUrl()}/sync`,
            {provider},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this)
            .catch(err => {
                throw err.response.data.error;
            });
    }

    static fetchCurrent(company) {
        const payrolls = new Payrolls(null, {
            company,
            pagination: false
        });

        return payrolls.fetch({
            filter: {
                where: {
                    status: {
                        lte: 2
                    }
                },
                order: 'createdAt DESC',
                limit: 1
            }
        })
            .then(() => {
                return Promise.resolve(payrolls.at(0));
            });
    }

    static getCurrentPeriodDate() {
        return Payroll.getPeriodFromDate(moment());
    }

    static getPeriodFromDate(date) {
        const m = moment(date || Date.now()).tz('Asia/Kuala_Lumpur');

        if (m.date() < config.periodStartDate) {
            m.subtract(1, 'month');
        }

        return m.toDate();
    }

    save() {
        // 20210706 PPKAN-290 make sure invoice ids are set to null
        if (this.get('chargebeeInvoiceId') === '') {
            this.set('chargebeeInvoiceId', null);
        }
        if (this.get('chargebeeAdjustmentInvoiceId') === '') {
            this.set('chargebeeAdjustmentInvoiceId', null);
        }

        return super.save(...arguments);
    }
}



// WEBPACK FOOTER //
// ./app/new-models/payroll.js